import { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Typography, message } from 'antd';

import type { Lineitem } from '../../interfaces/Ordel.Interface';
import {
	PROCESS_LABELS,
	type ProcessSpecRef,
} from '../../interfaces/ProcessCatalog.Interface';
import { useUpdateOrderLineItemMutation } from '../../redux/api/order';
import { useGetProcessSpecsQuery } from '../../redux/api/processCatalog';
import { extractApiError } from '../../utils/apiError';
import ProcessSpecSelect from './ProcessSpecSelect';

/** The two pickable kinds. Finishing is resolved from the surface finish, so it
 *  has no picker — correcting the finish is how you correct the finishing. */
type PickableKind = 'handling' | 'cleaning';

const SPEC_FIELDS: Record<PickableKind, 'handling_spec' | 'cleaning_spec'> = {
	handling: 'handling_spec',
	cleaning: 'cleaning_spec',
};

const INLINE_SELECT_WIDTH = 220;

/** Only what this cell reads, so both the v1 and v2 detail payloads fit. */
type ProcessLineItem = Pick<
	Lineitem,
	'id' | 'handling_spec' | 'cleaning_spec'
>;

interface Props {
	kind: PickableKind;
	lineItem: ProcessLineItem;
	disabled?: boolean;
	refetch: () => void;
	/** Summary = label above the select (details grid). Inline = label beside (Production). */
	layout?: 'summary' | 'inline';
}

/**
 * Handling / cleaning picker for a line item. Shown in the Production block on
 * the line item details page; special rows are marked in the open list by
 * ProcessSpecSelect.
 */
export default function LineItemProcessSelect({
	kind,
	lineItem,
	disabled = false,
	refetch,
	layout = 'summary',
}: Props) {
	const [updateOrderLineItem, { isLoading }] = useUpdateOrderLineItemMutation();
	const [messageApi, messageContext] = message.useMessage();
	const { data: specs } = useGetProcessSpecsQuery({ kind, is_active: true });

	const field = SPEC_FIELDS[kind];
	const savedRef = lineItem[field] ?? null;
	const savedId = savedRef?.id ?? null;
	const [draftId, setDraftId] = useState<string | null>(savedId);

	useEffect(() => {
		setDraftId(savedId);
	}, [savedId]);

	const draftValue = useMemo((): ProcessSpecRef | null | undefined => {
		if (draftId === null) {
			return null;
		}
		if (savedRef?.id === draftId) {
			return savedRef;
		}
		const spec = specs?.find((row) => row.id === draftId);
		if (!spec) {
			return null;
		}
		return {
			id: spec.id,
			kind: spec.kind,
			name: spec.name,
			behavior: spec.behavior,
			is_special: spec.is_special,
			batch_mode: spec.batch_mode,
			is_active: spec.is_active,
		};
	}, [draftId, savedRef, specs]);

	const dirty = draftId !== savedId;

	async function save() {
		try {
			await updateOrderLineItem({
				id: lineItem.id,
				values: { [field]: draftId },
			}).unwrap();
			refetch();
			messageApi.open({ type: 'success', content: 'Updated' });
		} catch (error) {
			messageApi.open({ type: 'error', content: extractApiError(error) });
		}
	}

	const selectWidth = layout === 'inline' ? INLINE_SELECT_WIDTH : 190;

	const select = (
		<ProcessSpecSelect
			kind={kind}
			value={draftValue}
			onChange={(specId) => setDraftId(specId)}
			disabled={disabled}
			loading={isLoading}
			width={selectWidth}
			allowClear
		/>
	);

	const saveButton = dirty ? (
		<Button
			type="primary"
			loading={isLoading}
			disabled={disabled || isLoading}
			onClick={save}
		>
			Save
		</Button>
	) : null;

	if (layout === 'inline') {
		return (
			<Flex align="center" gap="small" wrap="wrap">
				{messageContext}
				<Typography.Text style={{ minWidth: INLINE_SELECT_WIDTH }}>
					{PROCESS_LABELS[kind]}
				</Typography.Text>
				{select}
				{saveButton}
			</Flex>
		);
	}

	return (
		<Flex vertical align="start" gap={4}>
			{messageContext}
			<span className="text-slate-400">{PROCESS_LABELS[kind]}</span>
			<Flex align="center" gap="small" wrap="wrap">
				{select}
				{saveButton}
			</Flex>
		</Flex>
	);
}
